import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface DrPlanRow {
  readonly drPlanId: string;
  readonly drPlanCode: string;
  readonly drPlanName: string;
  readonly recoveryType: string;
  readonly rtoMinutes: number;
  readonly rpoMinutes: number;
  readonly status: string;
  readonly versionNo: number;
  readonly lastTestedAt: string | null;
}

const TYPES = ['', 'SITE_FAILURE', 'DATABASE', 'APPLICATION', 'INTEGRATION', 'STORAGE', 'SECURITY', 'IDENTITY', 'FULL_PLATFORM'];
const STATUSES = ['', 'DRAFT', 'UNDER_REVIEW', 'APPROVED', 'RETIRED'];
const CREATE_TYPES = TYPES.filter(Boolean);

/**
 * M31 — Disaster Recovery Dashboard (§10 / §31). Register of DR plans by
 * recovery type with their RTO/RPO targets, approval status, version and last
 * tested date (BR-04: periodic testing is mandatory). Supports authoring a new
 * DR plan and driving its lifecycle — submit for review, approve, record a test
 * and retire — each wired to the guarded /v1/bcm/dr-plans write API. Filterable
 * by recovery type and status.
 */
export function DrDashboardPage(): ReactNode {
  const { t: tr } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { atDay } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<DrPlanRow[]>([]);
  const [recoveryType, setRecoveryType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('APPLICATION');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (recoveryType) params.set('recoveryType', recoveryType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: DrPlanRow[] }>(`/v1/bcm/dr-plans${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, recoveryType, status, reload]);

  const failMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m31.drDashboard.err403')
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newName.trim()) { setActionError(tr('m31.drDashboard.errName')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/bcm/dr-plans`, {
        method: 'POST',
        body: { drPlanName: newName.trim(), recoveryType: newType },
        token,
      });
      setNotice(tr('m31.drDashboard.noticeCreated', { name: newName.trim() }));
      setNewName('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: DrPlanRow, path: string, label: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/bcm/dr-plans/${r.drPlanId}/${path}`, { method: 'POST', body: {}, token });
      setNotice(tr('m31.drDashboard.actNotice', { code: r.drPlanCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    }
  };

  return (
    <main aria-label="dr-dashboard">
      <h1>{tr('m31.drDashboard.title')}</h1>

      <section aria-label="create-dr-plan">
        <h2>{tr('m31.drDashboard.createHeading')}</h2>
        <form aria-label="dr-plan-form" onSubmit={(e) => void create(e)}>
          <label>{tr('m31.drDashboard.fieldName')}<input aria-label="field-dr-plan-name" value={newName} onChange={(e) => setNewName(e.target.value)} /></label>
          <label>{tr('m31.drDashboard.fieldRecoveryType')}
            <select aria-label="field-recovery-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-dr-plan" disabled={busy}>{busy ? tr('m31.drDashboard.busyCreate') : tr('m31.drDashboard.submitCreate')}</button>
        </form>
        {actionError && <p role="alert" aria-label="dr-plan-error">{actionError}</p>}
        {notice && <p aria-label="dr-plan-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m31.drDashboard.filterRecoveryType')}
          <select aria-label="filter-recovery-type" value={recoveryType} onChange={(e) => setRecoveryType(e.target.value)}>
            {TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m31.drDashboard.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m31.drDashboard.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m31.drDashboard.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m31.drDashboard.loading')}</p> : (
        <>
          <p aria-label="dr-plan-count">{tr('m31.drDashboard.count', { count: rows.length })}</p>
          <table aria-label="dr-plan-table">
            <thead><tr><th>{tr('m31.drDashboard.thCode')}</th><th>{tr('m31.drDashboard.thName')}</th><th>{tr('m31.drDashboard.thType')}</th><th>{tr('m31.drDashboard.thRto')}</th><th>{tr('m31.drDashboard.thRpo')}</th><th>{tr('m31.drDashboard.thVersion')}</th><th>{tr('m31.drDashboard.thStatus')}</th><th>{tr('m31.drDashboard.thLastTested')}</th><th>{tr('m31.drDashboard.thActions')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.drPlanId}>
                  <td>{r.drPlanCode}</td><td>{r.drPlanName}</td><td>{r.recoveryType}</td>
                  <td>{r.rtoMinutes}m</td><td>{r.rpoMinutes}m</td><td>v{r.versionNo}</td>
                  <td>{r.status}</td>
                  <td>{r.lastTestedAt ? atDay(r.lastTestedAt) : tr('m31.drDashboard.never')}</td>
                  <td>
                    {r.status === 'DRAFT' && (
                      <button type="button" aria-label={`submit-${r.drPlanCode}`} onClick={() => void act(r, 'submit', tr('m31.drDashboard.submitted'))}>{tr('m31.drDashboard.submit')}</button>
                    )}
                    {r.status === 'UNDER_REVIEW' && (
                      <button type="button" aria-label={`approve-${r.drPlanCode}`} onClick={() => void act(r, 'approve', tr('m31.drDashboard.approvedLabel'))}>{tr('m31.drDashboard.approveBtn')}</button>
                    )}
                    {r.status === 'APPROVED' && (
                      <>
                        <button type="button" aria-label={`record-test-${r.drPlanCode}`} onClick={() => void act(r, 'record-test', tr('m31.drDashboard.testRecorded'))}>{tr('m31.drDashboard.recordTest')}</button>
                        <button type="button" aria-label={`retire-${r.drPlanCode}`} onClick={() => void act(r, 'retire', tr('m31.drDashboard.retired'))}>{tr('m31.drDashboard.retire')}</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
